import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
 PieChart as LucidePieChart,
  IndianRupee,
  Package,
  Receipt,
  UtensilsCrossed,
  BarChart3,
  Activity,
  ArrowUp,
  ArrowDown,
  Calendar,
  X,
  Lock,
  Download,
  Search as SearchIcon,
  TrendingUp,
  Home,
  Sparkles,
  ArrowRight,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import PermissionDenied from '../../common/components/PermissionDenied';
import { API_URL } from '../../api/config';
import SessionTimerIndicator from '../settings/settingModal/SessionTimerIndicator';

const CHART_COLORS = ['#4169E1', '#60A5FA', '#34D399', '#E5E7EB'];

const CustomDateRangePicker = ({ startDate, endDate, onDateChange, onClose }) => {
  const [localStartDate, setLocalStartDate] = useState(startDate || '');
  const [localEndDate, setLocalEndDate] = useState(endDate || '');
  const [error, setError] = useState('');

  const handleApply = () => {
    if (!localStartDate || !localEndDate) {
      setError('Please select both start and end dates');
      return;
    }
    
    if (new Date(localStartDate) > new Date(localEndDate)) {
      setError('Start date cannot be after end date');
      return;
    }
    
    if (new Date(localEndDate) > new Date()) {
      setError('End date cannot be in the future');
      return;
    }
    
    setError('');
    onDateChange(localStartDate, localEndDate);
    onClose();
  };

  const handleClear = () => {
    setLocalStartDate('');
    setLocalEndDate('');
    setError('');
  };

  const maxDate = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-96">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Select Date Range</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Start Date</label>
            <input
              type="date"
              value={localStartDate}
              onChange={(e) => setLocalStartDate(e.target.value)}
              max={maxDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-2">End Date</label>
            <input
              type="date"
              value={localEndDate}
              onChange={(e) => setLocalEndDate(e.target.value)}
              max={maxDate}
              min={localStartDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</div>
          )}
          
          <div className="flex items-center justify-between pt-4">
            <button onClick={handleClear} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm">
              Clear
            </button>
            <div className="space-x-2">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleApply} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SalesReport = () => {
  const { userToken: token } = useAuth();
  const navigate = useNavigate();
  const [salesData, setSalesData] = useState([]);
  const [reportTotals, setReportTotals] = useState({
    totalBills: 0,
    totalRevenue: 0,
    totalItemsSold: 0,
    averageOrderValue: 0
  });
const [timeFilter, setTimeFilter] = useState('lastMonth');  
  const [sortConfig, setSortConfig] = useState({ key: 'totalRevenue', direction: 'desc' });
  
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isUsingCustomRange, setIsUsingCustomRange] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailedError, setDetailedError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All Items');

  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    if (token) {
      checkSubscriptionStatus();
    }
  }, [token]);

  useEffect(() => {
    if (token && subscriptionStatus && (subscriptionStatus.status === 'active' || subscriptionStatus.status === 'trial')) {
      fetchData();
    }
  }, [timeFilter, customStartDate, customEndDate, token, subscriptionStatus]);

  const checkSubscriptionStatus = async () => {
    try {
      setCheckingSubscription(true);
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch subscription status');
      
      const data = await response.json();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setError(error.message);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleSubscribe = () => {
    navigate('/subscription');
  };

const fetchData = async () => {
  if (!token) {
    setError('Authentication token is missing');
    return;
  }

  setIsLoading(true);
  setError(null);
  setDetailedError(null);
  
  try {
    let url;
    
    if (isUsingCustomRange && customStartDate && customEndDate) {
      url = `${API_URL}/reports/item-sales?startDate=${customStartDate}&endDate=${customEndDate}&sortBy=revenue`;
    } else {
      // Get current date in local timezone
      const now = new Date();
      let startDate, endDate;
      
      switch (timeFilter) {
        case 'today':
          // Today: 12:00 AM to current time (same day)
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          break;
          
        case 'yesterday':
          // Yesterday: previous day only
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
          endDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
          break;
          
        case 'lastWeek':
          // Last 7 days
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 7);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          break;
          
        case 'lastMonth':
          // Last 30 days
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 30);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          break;
          
        case 'lastSixMonths':
          // Last 6 months
          startDate = new Date(now);
          startDate.setMonth(startDate.getMonth() - 6);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          break;
          
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      }
      
      // Format dates as YYYY-MM-DD
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      console.log('Date filter:', timeFilter);
      console.log('Start Date:', startDate, '→', formatDate(startDate));
      console.log('End Date:', endDate, '→', formatDate(endDate));
      
      url = `${API_URL}/reports/item-sales?startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}&sortBy=revenue`;
    }

    console.log('API URL:', url);

    const response = await fetch(url, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const responseText = await response.text();

    if (!response.ok) {
      let errorMessage = `Failed to fetch sales data: ${response.status}`;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage = errorData.message || errorData.error || errorMessage;
        setDetailedError(errorData);
      } catch (e) {
        setDetailedError({ raw: responseText.substring(0, 500) });
      }
      if (response.status === 403 || errorMessage.toLowerCase().includes('insufficient permissions') || errorMessage.toLowerCase().includes('access denied')) {
        errorMessage = 'Access denied: contact admin for permissions';
      }
      throw new Error(errorMessage);
    }
    
    const result = JSON.parse(responseText);
    
    if (result.success && result.data) {
      setSalesData(result.data || []);
      setReportTotals(result.totals || {
        totalBills: 0,
        totalRevenue: 0,
        totalItemsSold: 0,
        averageOrderValue: 0
      });
    } else if (result.data) {
      setSalesData(result.data || []);
      setReportTotals(result.totals || {
        totalBills: 0,
        totalRevenue: 0,
        totalItemsSold: 0,
        averageOrderValue: 0
      });
    } else {
      setSalesData([]);
      setReportTotals({
        totalBills: 0,
        totalRevenue: 0,
        totalItemsSold: 0,
        averageOrderValue: 0
      });
    }
  } catch (error) {
    console.error('Error fetching sales data:', error);
    setError(error.message);
  } finally {
    setIsLoading(false);
  }
};

  const handleCustomDateChange = (startDate, endDate) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    setIsUsingCustomRange(true);
    setTimeFilter('');
    setCurrentPage(1);
  };

  const handlePredefinedFilterChange = (filter) => {
    setTimeFilter(filter);
    setIsUsingCustomRange(false);
    setCustomStartDate('');
    setCustomEndDate('');
    setCurrentPage(1);
  };

  const getChartData = () => {
    const sortedItems = [...salesData]
      .sort((a, b) => (b.totalRevenue || 0) - (a.totalRevenue || 0));
    
    if (sortedItems.length <= 3) {
      return sortedItems.map(item => ({
        name: item.itemName,
        value: parseFloat(item.totalRevenue) || 0
      }));
    } else {
      const topItems = sortedItems.slice(0, 3);
      const othersRevenue = sortedItems.slice(3).reduce((sum, item) => sum + (parseFloat(item.totalRevenue) || 0), 0);
      
      const result = topItems.map(item => ({
        name: item.itemName,
        value: parseFloat(item.totalRevenue) || 0
      }));
      
      if (othersRevenue > 0) {
        result.push({
          name: 'Others',
          value: othersRevenue
        });
      }
      
      return result;
    }
  };

  const sortData = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const getFilteredItems = () => {
    let filtered = [...salesData];
    
    if (categoryFilter !== 'All Items') {
      filtered = filtered.filter(item => {
        if (categoryFilter === 'Food') return item.category === 'Veg' || item.category === 'Non-Veg';
        if (categoryFilter === 'Beverage') return item.category === 'Beverage';
        return true;
      });
    }
    
    if (searchQuery) {
      filtered = filtered.filter(item => 
        item.itemName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  };

  const getSortedItems = () => {
    const filtered = getFilteredItems();
    const { key, direction } = sortConfig;
    return filtered.sort((a, b) => {
      const aVal = parseFloat(a[key]) || a[key] || 0;
      const bVal = parseFloat(b[key]) || b[key] || 0;
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getPaginatedItems = () => {
    const sortedItems = getSortedItems();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedItems.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(getSortedItems().length / itemsPerPage);

const getTimeFilterLabel = (filter) => {
  switch (filter) {
    case 'today': return 'Today';
    case 'yesterday': return 'Yesterday';
    case 'lastWeek': return 'Last Week';
    case 'lastMonth': return 'Last Month';
    case 'lastSixMonths': return 'Last 6 Months';
    default: return filter;
  }
};

  const formatCustomDateRange = () => {
    if (!customStartDate || !customEndDate) return '';
    const start = new Date(customStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = new Date(customEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${start} - ${end}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 text-sm">{data.name}</p>
          <p className="text-xs text-gray-600 mt-1">
            ₹{parseFloat(data.value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </p>
        </div>
      );
    }
    return null;
  };

  const DonutChart = () => {
    const data = getChartData();
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    // Calculate percentages and angles for the donut segments
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    let currentAngle = 0;
    
    const segments = data.map((item, index) => {
      const percentage = total > 0 ? (item.value / total) : 0;
      const dashArray = circumference;
      const dashOffset = circumference * (1 - percentage);
      const rotation = currentAngle;
      currentAngle += percentage * 360;
      
      return {
        ...item,
        percentage,
        dashArray,
        dashOffset,
        rotation,
        color: CHART_COLORS[index % CHART_COLORS.length]
      };
    });

    return (
      <div className="flex items-center gap-12">
        {/* SVG Donut Chart */}
        <div className="relative w-72 h-72 shrink-0">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            {/* Background circle */}
            <circle 
              cx="50" 
              cy="50" 
              r={radius} 
              fill="transparent" 
              stroke="#F1F5F9" 
              strokeWidth="12" 
            />
            
            {/* Data segments */}
            {segments.map((segment, index) => (
              <circle
                key={index}
                cx="50"
                cy="50"
                r={radius}
                fill="transparent"
                stroke={segment.color}
                strokeWidth="12"
                strokeDasharray={segment.dashArray}
                strokeDashoffset={segment.dashOffset}
                strokeLinecap="round"
                style={{
                  transformOrigin: 'center',
                  transform: `rotate(${segment.rotation}deg)`
                }}
              />
            ))}
          </svg>
          
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Total Sales</p>
            <p className="text-3xl font-bold text-gray-900">₹{(total).toFixed(0)}</p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-4">
          {segments.map((segment, index) => {
            const percentage = (segment.percentage * 100).toFixed(0);
            return (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: segment.color }}
                  />
                  <span className="text-sm font-medium text-gray-700">{segment.name}</span>
                  <span className="text-sm font-semibold text-gray-900">{percentage}%</span>
                </div>
                <span className="text-sm text-gray-500">
                  ₹{parseFloat(segment.value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-8 text-center">
          <div className="text-red-500 mb-4 text-xl font-semibold">Authentication Required</div>
          <p className="text-gray-600">Please log in to view the sales report.</p>
        </div>
      </div>
    );
  }

  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking subscription...</p>
        </div>
      </div>
    );
  }

  if (!subscriptionStatus || (subscriptionStatus.status !== 'active' && subscriptionStatus.status !== 'trial')) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-8 text-center">
          <div className="bg-blue-50 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Lock className="text-blue-600" size={32} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Premium Feature</h2>
          <p className="text-gray-600 mb-6">Sales Report is a premium feature. Subscribe to access detailed analytics.</p>
          <button 
            onClick={handleSubscribe}
            className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
          >
            {subscriptionStatus?.canStartTrial ? 'Start Free Trial' : 'Subscribe Now'}
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <PermissionDenied
        variant="card"
        title="Error Loading Report"
        message={error}
        onRetry={fetchData}
        onGoHome={() => navigate('/')}
        isRetrying={isLoading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="min-h-screen bg-slate-50 p-8">
      <SessionTimerIndicator module="expenseReport" />
      <div className="w-full px-8 py-6 space-y-6">
     {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Home size={14} className="text-gray-400" />
              <ChevronRight size={14} />
              <span>Reports</span>
              <ChevronRight size={14} />
              <span className="text-gray-900">Item-wise Sales</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Item-wise Sales Report</h1>
            <p className="text-sm text-gray-500 mt-1">Analyze sales volume and revenue by individual menu items to identify top performers.</p>
          </div>
            <div className="flex items-center gap-3">
  <div className="bg-white border border-gray-200 rounded-lg p-0.5 flex">
    {['today', 'yesterday', 'lastWeek', 'lastMonth', 'lastSixMonths'].map((range) => (
      <button
        key={range}
        onClick={() => handlePredefinedFilterChange(range)}
        className={`px-4 py-2 rounded-md text-sm font-medium transition ${
          timeFilter === range && !isUsingCustomRange
            ? 'bg-blue-600 text-white'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {getTimeFilterLabel(range)}
      </button>
    ))}
  </div>
  <button
    onClick={() => setShowDatePicker(true)}
    className="bg-white border border-gray-200 px-4 py-2 rounded-lg flex items-center gap-2 text-sm text-gray-700 hover:bg-gray-50"
  >
    <Calendar size={16} />
    {isUsingCustomRange ? formatCustomDateRange() : 'Custom'}
  </button>
  <button className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm hover:bg-blue-700">
    <Download size={16} />
    Export
  </button>
</div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
  <IndianRupee size={18} className="text-green-600" strokeWidth={1.75} />
</div>
                  <div className="flex items-center gap-1 text-xs font-medium text-green-600">
                    <TrendingUp size={12} />
                    12.5%
                  </div>
                </div>
                <p className="text-xs font-medium text-gray-500 uppercase">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ₹{parseFloat(reportTotals.totalRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
  <Package size={18} className="text-blue-600" strokeWidth={1.75} />
</div>
                  <div className="flex items-center gap-1 text-xs font-medium text-green-600">
                    <TrendingUp size={12} />
                    5.2%
                  </div>
                </div>
                <p className="text-xs font-medium text-gray-500 uppercase">Items Sold</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {parseInt(reportTotals.totalItemsSold || 0).toLocaleString('en-IN')}
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
  <Receipt size={18} className="text-amber-600" strokeWidth={1.75} />
</div>
                  <div className="text-xs font-medium text-gray-400">— 0.0%</div>
                </div>
                <p className="text-xs font-medium text-gray-500 uppercase">Avg. Item Price</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ₹{parseFloat(reportTotals.averageOrderValue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-3 gap-6">
              {/* Sales Distribution */}
              <div className="col-span-2 bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Sales Distribution</h3>
                  <div className="bg-gray-50 rounded-lg p-0.5 flex gap-0.5">
                    <button className="p-2 rounded bg-white shadow-sm">
                      <LucidePieChart size={16} className="text-gray-700" />
                    </button>
                    <button className="p-2 rounded text-gray-400 hover:text-gray-600">
                      <BarChart3 size={16} />
                    </button>
                    <button className="p-2 rounded text-gray-400 hover:text-gray-600">
                      <Activity size={16} />
                    </button>
                  </div>
                </div>
                
                {salesData.length > 0 ? (
                  <DonutChart />
                ) : (
                  <div className="flex justify-center items-center h-48 text-gray-400">
                    No data available
                  </div>
                )}
              </div>

              {/* Top Performers */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Top Performers</h3>
                  <button className="text-xs font-medium text-blue-600 uppercase">View All</button>
                </div>
                
                <div className="space-y-4">
                  {getSortedItems().slice(0, 4).map((item, index) => {
                    const colors = ['bg-blue-600', 'bg-blue-500', 'bg-emerald-400', 'bg-gray-300'];
                    const maxRevenue = parseFloat(getSortedItems()[0]?.totalRevenue || 1);
                    const width = ((parseFloat(item.totalRevenue) || 0) / maxRevenue) * 100;
                    
                    return (
                      <div key={index}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                           <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
  <UtensilsCrossed size={18} className="text-orange-400" strokeWidth={1.75} />
</div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{item.itemName}</p>
                              <p className="text-xs text-gray-500 uppercase">
                                {parseInt(item.totalQuantity || 0)} orders
                              </p>
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            ₹{parseFloat(item.totalRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${colors[index]} rounded-full transition-all`}
                            style={{ width: `${width}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* AI Insight */}
            {salesData.length > 0 && (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-indigo-600 shadow-sm">
                    <Sparkles size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-indigo-600 uppercase mb-1">AI Insight</p>
                    <p className="text-sm text-gray-700">
                      Sales for <span className="font-semibold text-gray-900">"{getSortedItems()[0]?.itemName}"</span> are up{' '}
                      <span className="text-green-600 font-semibold">up</span> this week. Consider stocking up on inventory.
                    </p>
                    <button className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 uppercase mt-2 hover:text-indigo-700">
                      Apply Strategy
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Detailed Breakdown</h3>
                  <div className="flex items-center gap-4">
                    <div className="bg-gray-50 rounded-lg p-0.5 flex gap-0.5">
                      {['All Items', 'Food', 'Beverage'].map(cat => (
                        <button
                          key={cat}
                          onClick={() => {
                            setCategoryFilter(cat);
                            setCurrentPage(1);
                          }}
                          className={`px-3 py-1.5 rounded text-xs font-medium ${
                            categoryFilter === cat ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input 
                        type="text" 
                        placeholder="Search item..." 
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th 
                        onClick={() => sortData('itemName')}
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Item Name
                          {sortConfig.key === 'itemName' && (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => sortData('category')}
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Category
                          {sortConfig.key === 'category' && (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => sortData('totalQuantity')}
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Qty Sold
                          {sortConfig.key === 'totalQuantity' && (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => sortData('totalRevenue')}
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Revenue
                          {sortConfig.key === 'totalRevenue' && (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          )}
                        </div>
                      </th>
                      <th 
                        onClick={() => sortData('averagePrice')}
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          Avg Price
                          {sortConfig.key === 'averagePrice' && (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getPaginatedItems().map((row, i) => (
                      <tr key={`${row.productId}-${i}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
  <UtensilsCrossed size={15} className="text-orange-400" strokeWidth={1.75} />
</div>
                            <span className="text-sm font-medium text-gray-900 truncate max-w-xs" title={row.itemName}>
                              {row.itemName || 'Unknown Item'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{row.category || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {parseInt(row.totalQuantity || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                          ₹{parseFloat(row.totalRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          ₹{parseFloat(row.averagePrice || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                            TRENDING
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    Page <span className="font-medium text-gray-900">{currentPage}</span> of{' '}
                    <span className="font-medium text-gray-900">{totalPages}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3.5 py-2 rounded-lg text-sm font-medium ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showDatePicker && (
        <CustomDateRangePicker
          startDate={customStartDate}
          endDate={customEndDate}
          onDateChange={handleCustomDateChange}
          onClose={() => setShowDatePicker(false)}
        />
      )}
    </div>
    </div>
  );
};

export default SalesReport;